import { useEffect, useMemo, useState } from 'react';
import classes from './App.module.css';
import PostCard from './components/card/PostCard';
import { serverAccessor } from './accessors/serverAccessor';
import type PostInterface from './interfaces/PostInterface';
import {
  Container,
  Stack,
  Title,
  Text,
  SimpleGrid,
  Group,
  TextInput,
  SegmentedControl,
  Button,
  Skeleton,
  Center,
  Badge,
  ActionIcon,
  Space,
} from '@mantine/core';
import { IconSearch, IconMail, IconPhone, IconArrowUp } from '@tabler/icons-react';
import { Trans, useTranslation } from 'react-i18next';
import { trackEvent } from './utils/analytics.ts';
import Seo from './components/Seo.tsx';

export default function App() {
  const [posts, setPosts] = useState<PostInterface[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<'new' | 'title'>('new');
  const { t } = useTranslation();

  const seoTitle = t('seo.homeTitle');
  const seoDescription = t('seo.homeDescription');

  const handleContactClick = (method: 'phone' | 'email', context: string) => {
    trackEvent('contact_click', { category: 'Contact', label: `${context}:${method}` });
  };

  const handleSortChange = (value: string) => {
    setSort(value as 'new' | 'title');
    trackEvent('list_sort_change', { category: 'Listings', label: value });
  };

  const handleScrollToTop = () => {
    trackEvent('scroll_to_top', { category: 'Navigation' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    (async () => {
      try {
        const data = await serverAccessor.getAllPosts();
        setPosts(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    let arr = posts;

    if (query) {
      arr = arr.filter(
        (p) =>
          p.title.toLowerCase().includes(query) ||
          p.description.toLowerCase().includes(query) ||
          p.postId.toLowerCase().includes(query)
      );
    }

    if (sort === 'title') {
      arr = [...arr].sort((a, b) => a.title.localeCompare(b.title));
    } else {
      // If you have createdAt, use that here. For now, a stable ref fallback:
      arr = [...arr].sort((a, b) => b.postId.localeCompare(a.postId));
    }

    return arr;
  }, [posts, q, sort]);

  return (
    <>
      <Seo title={seoTitle} description={seoDescription} canonicalPath="/" />
      <div className={classes.page}>
      {/* HERO */}
      <section className={classes.hero}>
        <Container size="xl" className={classes.heroInner}>
          <Stack gap={8} className={classes.heroText}>
            <span className={classes.kicker}>promociones montosa</span>
            <Title order={1} className={classes.title}>
              <Trans i18nKey="hero_title">
                Hogares con carácter, <br /> ubicaciones con alma.
              </Trans>
            </Title>
            <Text className={classes.subtitle}>
              {t('hero_subtitle')}
            </Text>
            <Group gap="sm" mt="xs" wrap="wrap">
              <Button
                size="md"
                leftSection={<IconPhone size={16} />}
                component="a"
                href="tel:+34653784511"
                aria-label="Call Promonsa at +34 653 784 511"
                onClick={() => handleContactClick('phone', 'hero')}
              >
                +34 653 784 511
              </Button>
              <Button
                size="md"
                variant="light"
                leftSection={<IconMail size={16} />}
                component="a"
                href="mailto:info@promonsa.net"
                aria-label="Email Promonsa"
                onClick={() => handleContactClick('email', 'hero')}
              >
                info@promonsa.net
              </Button>
            </Group>
            <Group gap="xs" mt="sm">
              <Badge variant="light" color="green">{t('Up to date')}</Badge>
              <Badge variant="light">{t('Selected properties')}</Badge>
            </Group>
          </Stack>
        </Container>

        {/* FLOATING SEARCH BAR */}
        <div className={classes.controlsWrap}>
          <Container size="xl">
            <div className={classes.controls}>
              <TextInput
                value={q}
                onChange={(e) => setQ(e.currentTarget.value)}
                placeholder={t('home_search_bar_text')}
                leftSection={<IconSearch size={16} />}
                className={classes.search}
              />
              <SegmentedControl
                value={sort}
                onChange={handleSortChange}
                className={classes.segmented}
                data={[
                  { label: t('Most recent'), value: 'new' },
                  { label: t('Title A-Z'), value: 'title' },
                ]}
              />
            </div>
          </Container>
        </div>
      </section>

      {/* LIST */}
      <section className={classes.listSection}>
        <Container size="xl">
          {/* <Divider mb="lg" className={classes.divider} /> */}
          {loading ? (
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="xl">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} height={420} radius="xl" />
              ))}
            </SimpleGrid>
          ) : filtered.length === 0 ? (
            <Center py="5rem">
              <Stack align="center" gap={8}>
                <Title order={3}>
                  <Trans i18nKey="No results">
                    Sin resultados
                  </Trans>
                </Title>
                <Text c="dimmed" ta="center" maw={520}>
                  <Trans i18nKey="no_results_try_again">
                    Try with another search or tell us what you're looking for at <a href="mailto:info@promonsa.net" aria-label="Email Promonsa">info@promonsa.net</a>.
                  </Trans>
                </Text>
              </Stack>
            </Center>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="xl">
              {filtered.map((p) => (
                <PostCard key={p.postId} post={p} />
              ))}
            </SimpleGrid>
          )}
        </Container>
      </section>

      {/* CTA BAND */}
      <section className={classes.ctaBand}>
        <Container size="xl" className={classes.ctaInner}>
          <Stack gap={6}>
            <Title order={3} className={classes.ctaTitle}>
              {t('cant_find_what_youre_looking_for')}
            </Title>
            <Text className={classes.ctaText}>
              {t('home_page_cta_subtitle')}
            </Text>
          </Stack>
          <Space h="md" />
          <Group gap="sm" wrap="wrap">
            <Button
              size="md"
              leftSection={<IconPhone size={16} />}
              component="a"
              href="tel:+34615503852"
              aria-label="Call Promonsa at +34 615 503 852"
              onClick={() => handleContactClick('phone', 'cta_band')}
            >
              {t("Call")}
            </Button>
            <Button
              size="md"
              variant="light"
              leftSection={<IconMail size={16} />}
              component="a"
              href="mailto:info@promonsa.net"
              aria-label="Email Promonsa"
              onClick={() => handleContactClick('email', 'cta_band')}
            >
              {t("Email")}
            </Button>
          </Group>
        </Container>
      </section>

      {/* Back to top */}
      <ActionIcon
        size="lg"
        radius="xl"
        variant="filled"
        className={classes.toTop}
        onClick={handleScrollToTop}
        aria-label="Volver arriba"
      >
        <IconArrowUp size={18} />
      </ActionIcon>
    </div>
    </>
  );
}
